import { useMemo, useState } from "react";
import type { ReactNode } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import {
  Alert,
  BarChart,
  Card,
  CHART_NEGATIVE,
  CHART_POSITIVE,
  DataTable,
  DonutChart,
  EmptyState,
  Field,
  GroupedBarChart,
  PageHeader,
  Select,
  Sparkline,
  Spinner,
  StackedBarChart,
  StatTile,
} from "@empite/ui-components";
import type { Column, SeriesPoint, StatDelta } from "@empite/ui-components";
import type {
  AnalyticsDelta,
  AnalyticsOverview,
  AnalyticsTopUser,
  AnalyticsTopUsers,
  AnalyticsTotals,
  ModelUsageReport,
  Operator,
} from "../admin-client";
import { useAnalyticsOverview, useAnalyticsTopUsers, useModelUsage } from "../admin-hooks";
import { ShortID, StatusBadge, when } from "./common";
import { ErrorAlert, RequireOperator, can } from "./guards";
import { NotPermitted } from "./packages";
import { ASSUMED_EXPONENT, currencyExponent, formatUnits } from "./money-units";
import {
  activeUserSeries,
  moneySeries,
  outcomeSeries,
  platformParts,
  sameWindow,
  tokenSeries,
  topModelRequests,
} from "./analytics-charts";
import { deltaSentiment, formatDelta, formatRate, windowMeasured } from "./analytics-window";
import { IDENTITY, MONEY, READ, WITHHELD, WindowNotices, WindowPicker, money } from "./analytics-common";
import type { AnalyticsSearch, DeltaPolarity } from "./analytics-window";
import {
  DEFAULT_ROWS,
  DEFAULT_USER_SORT,
  ROW_OPTIONS,
  activeOrder,
  nextUserSort,
  resolveUserSort,
  rowIdentity,
  sortArrow,
  sortStateLabel,
  userSortColumns,
} from "./analytics-users";
import type { SortOrder, TopUserSort, UserSort } from "./analytics-users";

export type { AnalyticsSearch };

/**
 * READ gates the whole page rather than a control on it: every figure on the
 * screen comes from the one call behind it, so an operator without it has no
 * dashboard left to render — see NotPermitted. MONEY gates four of the eight
 * tiles, and the response's `money_visible` is what actually drives them. Both
 * are in analytics-common.tsx, because the cards on the account and
 * application pages are guarded with the same two.
 */
/**
 * The model usage report is a different route with a different grant, and the
 * one chart drawn from it is gated on holding it. Not because the dashboard
 * needs it — the other five charts render without it — but because the Admin
 * API's Guard writes every refusal to the audit trail, so firing a read the
 * operator cannot make fills that trail with denials nobody chose to attempt.
 */
const MODELS = "providers:read";

/**
 * Analytics — the platform's shape over a window the operator chooses.
 *
 * Every figure comes from the worker's nightly rollups rather than from the
 * request log, which is what makes leaving this open on a wall monitor cost
 * index range scans instead of a table walk.
 */
export function Analytics({ search }: { search: AnalyticsSearch }) {
  return (
    <RequireOperator>
      {(session) =>
        can(session.operator, READ) ? (
          <Dashboard search={search} operator={session.operator} />
        ) : (
          <NotPermitted title="Analytics" permission={READ} />
        )
      }
    </RequireOperator>
  );
}

/**
 * What the overview read can be, which is FOUR things and not two — the same
 * distinction models.tsx protects, for the same reason.
 *
 * "empty" is the one worth spelling out: an unmeasured window and a quiet
 * window both arrive as a document full of zeros, and drawing the zeros would
 * tell an operator that nobody used the platform on days the sweep had not
 * reached. `available_from` is what separates them, and it is checked before
 * a single tile is rendered.
 */
type OverviewReport =
  | { kind: "loading" }
  | { kind: "failed" }
  | { kind: "empty"; overview: AnalyticsOverview }
  | { kind: "ready"; overview: AnalyticsOverview };

function reportOf(data: AnalyticsOverview | undefined, error: unknown): OverviewReport {
  if (data) {
    return windowMeasured(data.available_from, data.window.until)
      ? { kind: "ready", overview: data }
      : { kind: "empty", overview: data };
  }
  return error ? { kind: "failed" } : { kind: "loading" };
}

function Dashboard({ search, operator }: { search: AnalyticsSearch; operator: Operator | undefined }) {
  const navigate = useNavigate();
  const overview = useAnalyticsOverview({
    since: search.since,
    until: search.until,
    days: search.days,
  });
  const report = reportOf(overview.data, overview.error);
  // The document, whatever the read made of it. The window banners are drawn
  // from it in the "empty" state as well as the "ready" one: a window that was
  // clamped is still a window that was clamped when nothing was measured in
  // it, and hiding the explanation is how an operator concludes the dashboard
  // is broken rather than that they asked about the wrong days.
  const data = overview.data;

  return (
    <>
      <PageHeader
        title="Analytics"
        description="How much of the platform is being used, by whom, on what devices, and what it earns. Every figure is a nightly rollup, so this page is cheap to leave open."
      />

      <Card title="Window">
        <WindowPicker
          search={search}
          busy={overview.isFetching}
          apply={(next) => void navigate({ to: "/analytics", search: next })}
        />
      </Card>

      <Card
        title={
          data
            ? `${data.window.since} to ${data.window.until} (${data.window.days} days)`
            : "Headline figures"
        }
      >
        {/* Loading, failed, empty and ready, in that order and never folded
            into one another. A failed read is not an empty window, and an
            empty window is not a quiet one. */}
        <ErrorAlert error={overview.error} title="The dashboard could not be loaded" />
        {data ? (
          <WindowNotices
            window={data.window}
            currency={data.currency}
            currencies={data.currencies}
            search={search}
          />
        ) : null}
        {report.kind === "loading" ? <Spinner label="Loading the dashboard" /> : null}
        {report.kind === "empty" ? (
          <EmptyState title="Nothing has been measured over these days">
            <p>
              {report.overview.available_from === null
                ? "No daily rollup has been written yet, so there is nothing to report — not even a zero. The worker writes them nightly; a deployment that has just been brought up has none until its first sweep runs."
                : `The rollups start on ${report.overview.available_from}, which is after this window ends. Ask about a later window to see figures.`}
            </p>
            <p>
              Zeros are not shown here on purpose: &ldquo;nobody used the platform&rdquo; and
              &ldquo;nobody had measured it yet&rdquo; are different facts, and a row of zeros
              cannot tell you which one you are looking at.
            </p>
          </EmptyState>
        ) : null}
        {report.kind === "ready" ? <Tiles overview={report.overview} /> : null}
      </Card>

      <Charts report={report} error={overview.error} operator={operator} />

      <TopUsersCard search={search} report={report} operator={operator} />
    </>
  );
}

/**
 * One measure's delta, or nothing.
 *
 * `undefined` in — a money delta withheld along with the figure it describes —
 * gives no delta line at all: a movement shown under an em dash would be a
 * change in a number the reader is not being shown.
 */
function delta(percent: number | null | undefined, polarity: DeltaPolarity): StatDelta | undefined {
  if (percent === undefined) {
    return undefined;
  }
  const formatted = formatDelta(percent);
  return {
    text: formatted.text,
    direction: formatted.direction,
    sentiment: deltaSentiment(formatted.direction, polarity),
  };
}

/** The eight headline figures. */
function Tiles({ overview }: { overview: AnalyticsOverview }) {
  const t: AnalyticsTotals = overview.totals;
  const d: AnalyticsDelta = overview.comparison.delta_percent;
  const visible = overview.money_visible;
  const currency = overview.currency;
  const previous = overview.comparison_window;

  return (
    <>
      <p className="esm-muted-text">
        Every change below is against {previous.since} to {previous.until}, the equal window
        immediately before this one.
      </p>
      <div className="esm-stat-grid">
        <StatTile
          label="Requests"
          value={t.requests.toLocaleString()}
          delta={delta(d.requests, "neutral")}
          hint={`${t.failed_requests.toLocaleString()} of them failed.`}
        />
        <StatTile
          label="Failure rate"
          value={formatRate(t.failure_rate)}
          unavailable="The rate could not be computed from this answer."
          // Up is bad here and nowhere else on this row: a rise in failures is
          // the one movement on the page that is unambiguously worse news.
          delta={delta(d.failure_rate, "up-bad")}
          hint="The change is in the rate itself, not in percentage points."
        />
        <StatTile
          label="Total tokens"
          value={t.total_tokens.toLocaleString()}
          delta={delta(d.total_tokens, "neutral")}
          hint={`${t.input_tokens.toLocaleString()} in, ${t.output_tokens.toLocaleString()} out, ${t.cached_input_tokens.toLocaleString()} cached, ${t.cache_write_tokens.toLocaleString()} cache writes, ${t.reasoning_tokens.toLocaleString()} reasoning.`}
        />
        <StatTile
          label="Active users"
          value={t.distinct_users.toLocaleString()}
          delta={delta(d.distinct_users, "neutral")}
          hint="An exact count of the accounts that made a request. Somebody who came back on a second day is one person."
        />
        <StatTile
          label="Active devices"
          // "up to", because the figure is an upper bound and the wire says so
          // in its name: no rollup carries device ids, so a device seen on two
          // days is counted twice and nothing in the schema can do better.
          value={`up to ${t.distinct_devices_upper_bound.toLocaleString()}`}
          delta={delta(d.distinct_devices_upper_bound, "neutral")}
          hint="An upper bound: a device seen on two days, or in two applications, is counted twice."
        />
        <StatTile
          label="Revenue"
          value={money(t.revenue_micros, visible, currency)}
          unavailable={WITHHELD}
          delta={delta(d.revenue_micros, "up-good")}
          hint="Settled inference revenue. Top-ups and subscription charges are money moving into a wallet, not revenue, and they are on the revenue screen."
        />
        <StatTile
          label="Provider cost"
          value={money(t.cost_micros, visible, currency)}
          unavailable={WITHHELD}
          // Neutral: cost rising alongside revenue is a platform doing more
          // business, and colouring it red would say otherwise.
          delta={delta(d.cost_micros, "neutral")}
          hint="What the same requests cost upstream."
        />
        <StatTile
          label="Margin"
          value={money(t.margin_micros, visible, currency)}
          unavailable={WITHHELD}
          delta={delta(d.margin_micros, "up-good")}
          hint="Revenue minus cost. Negative is a real reading — a written-off day costs money that was never charged for."
        />
      </div>
    </>
  );
}

/* -------------------------------------------------------------------------- *
 * The charts.
 *
 * Six pictures over two reads. Each one is its own Card and each one fails on
 * its own, which is the rule models.tsx sets: the usage figures failing is not
 * the catalogue failing, and a page that goes blank because one of its six
 * queries returned a 500 has hidden five answers it had.
 *
 * Every series comes out of analytics-charts.ts, which runs under `node` and
 * carries its own assertions. Nothing below computes a number.
 * -------------------------------------------------------------------------- */

/**
 * One chart in one Card, in the four states the read can be in.
 *
 * `snapshot` is the exception worth naming: the device fleet is counted LIVE
 * rather than rolled up per day, so it has a real answer on a window the
 * sweeps have not reached yet. Every other card has nothing to draw there and
 * says so rather than drawing the zeros.
 */
function ChartCard({
  title,
  description,
  report,
  error,
  snapshot = false,
  children,
}: {
  title: string;
  description?: ReactNode;
  report: OverviewReport;
  error: unknown;
  snapshot?: boolean;
  children: (overview: AnalyticsOverview) => ReactNode;
}) {
  const drawable =
    report.kind === "ready" || (snapshot && report.kind === "empty") ? report.overview : undefined;
  return (
    <Card title={title}>
      {description ? <p className="esm-muted-text">{description}</p> : null}
      {/* The alert is INSIDE this card, so the five beside it keep working.
          It repeats the one at the top of the page on purpose: a reader
          scrolled down to a blank card needs the reason where the blank is. */}
      {report.kind === "failed" ? (
        <ErrorAlert error={error} title="This chart could not be loaded" />
      ) : null}
      {/* The title as written, not lower-cased: "(USD)" is a currency code
          and a screen reader spells a lower-cased one out as a word. */}
      {report.kind === "loading" ? <Spinner label={`Loading ${title}`} /> : null}
      {report.kind === "empty" && !snapshot ? (
        <p className="esm-muted-text">
          Nothing has been measured over these days, so there is nothing to draw — not even a row of
          zeros, which would say the platform was idle rather than unmeasured.
        </p>
      ) : null}
      {drawable ? children(drawable) : null}
    </Card>
  );
}

/** Counts, with separators. The one formatter every non-money chart uses, so
 *  an axis tick and a table cell cannot disagree about what 12000 looks like. */
function count(value: number): string {
  return value.toLocaleString();
}

function Charts({
  report,
  error,
  operator,
}: {
  report: OverviewReport;
  error: unknown;
  operator: Operator | undefined;
}) {
  // "ready" and not "empty": the four window charts draw the window's rollups,
  // and an unmeasured window has none. The fleet donut takes its document
  // from ChartCard's `snapshot` path instead, and the model card below takes
  // only the window's LENGTH, which an unmeasured window still has.
  const ready = report.kind === "ready" ? report.overview : undefined;
  const answered = report.kind === "ready" || report.kind === "empty" ? report.overview : undefined;
  const days = answered?.window.days;

  // The sixth chart's read. Asked for the SAME number of days, which is not
  // the same as the same days — see the note the card renders when the two
  // windows disagree.
  const usage = useModelUsage(
    days === undefined ? {} : { window_days: days },
    can(operator, MODELS) && days !== undefined,
  );

  // Every derivation is keyed on the document itself, so a keystroke in the
  // window picker beside this — which is another component's state — cannot
  // rebuild a series, and nothing here re-fetches on a render.
  const outcome = useMemo(
    () => (ready ? outcomeSeries(ready.series, CHART_POSITIVE, CHART_NEGATIVE) : null),
    [ready],
  );
  const tokens = useMemo(() => (ready ? tokenSeries(ready.series) : null), [ready]);
  const money = useMemo(() => (ready ? moneySeries(ready.series) : null), [ready]);
  const users = useMemo(() => (ready ? activeUserSeries(ready.series) : null), [ready]);
  // Ranked in full and then cut, so the card can say how many aliases it is
  // NOT drawing. A slice with no count beside it reads as the whole list.
  const models = useMemo(() => {
    if (!usage.data) {
      return null;
    }
    const all = topModelRequests(usage.data.model_usage, 0);
    return { points: all.slice(0, TOP_MODELS), aliases: all.length };
  }, [usage.data]);

  // The money card is ABSENT for an operator without the grant, not empty and
  // not a row of placeholders: a withheld money FIGURE has a slot to sit in
  // and an explanation to put there, which is what the tiles above do, but a
  // card headed "Revenue" with nothing drawn in it invites the reading that
  // the platform earned nothing.
  //
  // The response decides once it has arrived — `money_visible` is the server's
  // answer and the only authority — and the operator's own permission list
  // decides before that, so a card that is going to appear does not pop in
  // after the read and shove the four below it down the page.
  const answeredMoney = answered?.money_visible;
  const moneyVisible = answeredMoney ?? can(operator, MONEY);
  const currency = answered?.currency;
  const moneyExponent = currencyExponent(currency) ?? ASSUMED_EXPONENT;
  const moneyFormat = (micros: number): string => formatUnits(micros, moneyExponent);
  // A window that earned nothing names no currency at all, and the figures are
  // then labelled "units" — the convention money-units.ts sets for exactly
  // this, and the same one the tiles above follow.
  const moneyUnit = currency ?? "units";

  return (
    <>
      <ChartCard
        title="Requests and failures per day"
        description="Every day of the window, including the quiet ones — a day with no traffic is drawn as the zero it was measured to be. Successes sit on the baseline; the failures cap each bar."
        report={report}
        error={error}
      >
        {() =>
          outcome === null ? null : (
            <StackedBarChart
              name="Requests by outcome"
              labels={outcome.labels}
              series={outcome.series}
              format={count}
              pointHeader="Day"
            />
          )
        }
      </ChartCard>

      <ChartCard
        title="Tokens in and out per day"
        description="In is the prompt, the part of it served from the provider's cache and the writes that filled it; out is the completion and the reasoning behind it. Every kind the platform bills for is in one of the two, so a bar's height is the Total tokens figure for that day — the five-way split is in the tile above."
        report={report}
        error={error}
      >
        {() =>
          tokens === null ? null : (
            <StackedBarChart
              name="Tokens by direction"
              labels={tokens.labels}
              series={tokens.series}
              format={count}
              pointHeader="Day"
            />
          )
        }
      </ChartCard>

      {moneyVisible ? (
        <ChartCard
          title={`Revenue, cost and margin per day (${moneyUnit})`}
          description="Side by side rather than stacked: margin is revenue minus cost rather than a part of it, and on a written-off day it is negative — which a stacked bar cannot draw at all. A bar below the line is a real reading."
          report={report}
          error={error}
        >
          {() =>
            money === null ? null : (
              <GroupedBarChart
                name={`Revenue, cost and margin per day, in ${moneyUnit}`}
                labels={money.labels}
                series={money.series}
                format={moneyFormat}
                // Money ticks, not counts: "1,250,000.00" needs a column half
                // as wide again as the default, and an end-anchored label
                // longer than its column runs off the edge of the drawing.
                valueAxis={128}
                pointHeader="Day"
              />
            )
          }
        </ChartCard>
      ) : null}

      <ChartCard
        title="Active users per day"
        description="An exact count of the accounts that made a request on each day. Shape only — the figures are in the table beneath it and the window's total is in the tile above."
        report={report}
        error={error}
      >
        {() =>
          users === null ? null : (
            <Sparkline
              name="Active users"
              points={users}
              format={count}
              width={720}
              height={120}
              pointHeader="Day"
            />
          )
        }
      </ChartCard>

      <ChartCard
        title="Devices by platform"
        description="The LIVE fleet, counted now — not a total over the window. It has an answer on days the rollups have not reached, which is why this card fills in when the others cannot."
        report={report}
        error={error}
        snapshot
      >
        {(overview) => (
          <>
            <DonutChart
              name="Live devices by platform"
              points={platformParts(overview.fleet)}
              format={count}
              centerLabel={count(overview.fleet.active_total)}
              centerCaption="live devices"
              pointHeader="Platform"
              valueHeader="Devices"
            />
            <p className="esm-muted-text">
              {count(overview.fleet.registered)} registered and {count(overview.fleet.revoked)}{" "}
              revoked between {overview.window.since} and {overview.window.until}. Those are
              movements inside the window; the ring is the population that is live right now, so the
              three figures are not expected to reconcile.
            </p>
          </>
        )}
      </ChartCard>

      <TopModelsCard
        usage={usage}
        models={models}
        permitted={can(operator, MODELS)}
        report={report}
      />
    </>
  );
}

/** How many aliases the model card draws. Eight bars is a chart; every alias
 *  the platform has ever routed is a list, and there is a screen for that. */
const TOP_MODELS = 8;

/**
 * The sixth chart, and the only one on a different read.
 *
 * It gets its own component rather than another ChartCard because its states
 * are not the overview's: it can be refused for want of a grant the rest of
 * the page does not need, it can be truncated, it draws a cut-down list, and
 * it can answer about a different span of days than the window above it.
 */
function TopModelsCard({
  usage,
  models,
  permitted,
  report,
}: {
  usage: { data?: ModelUsageReport; error: unknown; isPending: boolean };
  models: { points: SeriesPoint[]; aliases: number } | null;
  permitted: boolean;
  report: OverviewReport;
}) {
  const answer = usage.data;
  // The window this card can ask about comes from the overview — it sends the
  // same NUMBER of days — so an overview that has not arrived is a chart that
  // has not been asked for, rather than one that is loading.
  const window =
    report.kind === "ready" || report.kind === "empty" ? report.overview.window : undefined;
  // Two clamps and two end dates: this route stops at 365 days and always ends
  // yesterday, while the analytics window can be asked to end in the past. Ask
  // about a fortnight last March and the card beside this one describes a
  // different month, so it is said here rather than left to be assumed.
  const aligned = window === undefined || sameWindow(window, answer?.window);

  return (
    <Card title={`Top ${TOP_MODELS} models by requests`}>
      <p className="esm-muted-text">
        What was actually asked for, summed over every provider that served it — one alias can be
        answered by several provider models through fallback, and this counts the ask.
      </p>
      {/* Two different attempt rules meet on this page, and a reader comparing
          the bars against the tiles above deserves to be told which is which
          rather than to discover it as a discrepancy. See
          docs/operations/operations-and-analytics.md, "Two attempt rules". */}
      <p className="esm-muted-text">
        <strong>Per model, one bucket per request</strong> — attributed to the attempt that decided
        it. The totals above are <strong>per application, every attempt</strong>, so they can be the
        larger figure on a day a provider was billed for a call that then failed over. Neither is
        provider disagreement; the drift screen reads its own side from the usage records, not from
        these bars.
      </p>
      {!permitted ? (
        <p className="esm-muted-text">
          This one needs <code>{MODELS}</code>, which this account does not hold. The rest of the
          dashboard does not need it, and the cards above are unaffected.
        </p>
      ) : report.kind === "failed" ? (
        <Alert tone="warning" title="This chart was not asked for">
          It covers the same number of days as the window above, and that read failed — so there is
          no window to ask about yet.
        </Alert>
      ) : report.kind === "loading" ? (
        <Spinner label="Loading the model figures" />
      ) : (
        <>
          <ErrorAlert error={usage.error} title="This chart could not be loaded" />
          {answer && !aligned ? (
            <Alert tone="info" title="These are different days">
              This report covers {answer.window.since} to {answer.window.until}, which is not the
              window the rest of the page is answering. It always ends on the last complete day and
              cannot be asked about a span in the past.
            </Alert>
          ) : null}
          {answer?.truncated ? (
            <Alert tone="info" title="The usage report was cut short">
              More models carry traffic than one report returns, and the tail it dropped is the
              quietest end of it. Nothing missing from the bars below is necessarily at zero.
            </Alert>
          ) : null}
          {usage.isPending && !usage.error ? <Spinner label="Loading the model figures" /> : null}
          {models === null ? null : (
            <>
              <BarChart
                name="Requests by model alias"
                points={models.points}
                format={count}
                // Long enough for a provider-qualified alias, and a wider
                // figure column than the default because request counts on a
                // 90-day window run to seven digits.
                labelColumn={220}
                valueGutter={110}
                pointHeader="Alias"
                valueHeader="Requests"
              />
              {models.aliases > models.points.length ? (
                <p className="esm-chart__note">
                  {models.aliases - models.points.length} quieter{" "}
                  {models.aliases - models.points.length === 1 ? "alias is" : "aliases are"} not
                  drawn. The total in the summary is the total of the bars above, not of the
                  platform.
                </p>
              ) : null}
            </>
          )}
        </>
      )}
    </Card>
  );
}

/* -------------------------------------------------------------------------- *
 * Top users.
 *
 * The step from "the platform cost more this week" to "this account is why".
 * Every other card on this page is an aggregate; this one names people, and
 * both of the dashboard's omission rules land on it at once — the identity
 * columns follow `users:read` and the three money columns follow
 * `wallets:read`.
 * -------------------------------------------------------------------------- */

/* IDENTITY, from analytics-common.tsx, is the grant that decides whether the
 * rows below are people or ids. Unlike MONEY it does not remove a column: the
 * account column is what every row LINKS from, so it is always there and what
 * changes is what it can be labelled with. See `rowIdentity`. */

/**
 * A sortable heading, in the models screen's protocol.
 *
 * Written here rather than imported from models.tsx because the two sorts are
 * different mechanisms wearing the same clothes: that one re-orders a
 * catalogue the console already holds and has a third click that un-sorts it,
 * and this one is a QUERY PARAMETER — the route ranks and then cuts at
 * `limit`, so there is no local ordering to fall back to and no way to ask for
 * "no order". The protocol they do share is the part that matters to a
 * reader: a `<button>` inside the header cell, and the sort state in the
 * button's own accessible name, because `aria-sort` belongs on the `<th>` and
 * the shared `DataTable` owns that.
 */
function UserSortHeader({
  label,
  column,
  sort,
  onSort,
  title,
}: {
  label: string;
  column: TopUserSort;
  sort: UserSort;
  onSort: (column: TopUserSort) => void;
  title: string;
}) {
  const active: SortOrder | null = activeOrder(sort, column);
  return (
    <button
      type="button"
      className="esm-sort"
      title={title}
      aria-label={`${label} — ${sortStateLabel(column, active)}. Click to change the order.`}
      onClick={() => onSort(column)}
    >
      <span>{label}</span>
      <span className="esm-sort__arrow" aria-hidden="true">
        {sortArrow(active)}
      </span>
    </button>
  );
}

/** A money cell. The column only exists when `money_visible`, so an absent
 *  figure inside one is the server and the client disagreeing rather than a
 *  withheld field — said in as many words rather than drawn as a zero. */
function MoneyCell({ micros, exponent }: { micros: number | undefined; exponent: number }) {
  if (micros === undefined) {
    return <span className="esm-muted-text">unknown</span>;
  }
  return <span className="esm-money">{formatUnits(micros, exponent)}</span>;
}

/** The account column: a link in all three renders, because `user_id` is not
 *  gated and getting to the account is the whole point of the table. */
function AccountCell({
  row,
  identityVisible,
}: {
  row: AnalyticsTopUser;
  identityVisible: boolean;
}) {
  const identity = rowIdentity(row, identityVisible);
  return (
    <>
      <Link to="/users/$userId" params={{ userId: row.user_id }}>
        {identity.kind === "named" ? identity.name : <ShortID value={row.user_id} />}
      </Link>
      {identity.kind === "named" && identity.email ? (
        <div className="esm-muted-text">{identity.email}</div>
      ) : null}
      {identity.kind === "withheld" ? (
        // A stated placeholder, not an empty cell: an operator who sees a
        // blank column raises a bug against the dashboard, and one who sees
        // this asks for the grant. The whole table is still ranked and still
        // links, which is what makes it usable without it.
        <div className="esm-muted-text">Name and email withheld</div>
      ) : null}
      {identity.kind === "unnamed" ? (
        // A fact about the ACCOUNT rather than about the reader. A device-only
        // registration has neither field, and rendering it the same way as the
        // withheld case would send somebody hunting for a permission that
        // would show them nothing.
        <div className="esm-muted-text">No name or email on file</div>
      ) : null}
      <div className="esm-badge-row">
        <StatusBadge status={row.status} />
        {row.is_developer ? <StatusBadge status="developer" /> : null}
      </div>
    </>
  );
}

/**
 * The leaderboard.
 *
 * The ordering is a QUERY PARAMETER and not a client-side sort, which is the
 * one decision worth spelling out. This route ranks the whole window and then
 * cuts at `limit`, so the rows on screen are "the heaviest N by tokens" —
 * re-ordering those N by revenue in the browser would put "the ten heaviest
 * token users, arranged by revenue" under a heading reading "the top ten by
 * revenue", which is a different set of people. Nothing here sorts
 * `query.data`: it is react-query's cache, sorting it in place would reorder
 * it for every other reader of the same key, and there is nothing to sort in
 * the first place because the server already did it.
 */
function TopUsersCard({
  search,
  report,
  operator,
}: {
  search: AnalyticsSearch;
  report: OverviewReport;
  operator: Operator | undefined;
}) {
  const [sort, setSort] = useState<UserSort>(DEFAULT_USER_SORT);
  const [rows, setRows] = useState<number>(DEFAULT_ROWS);

  // Two authorities, on the rule the money chart above follows. The operator's
  // own permission list is the PREDICTION, available before any read, and it
  // is what stops three money columns appearing after the answer lands and
  // rearranging a table somebody is already reading. The response's
  // `money_visible` is the CORRECTION, and the only real authority.
  const predicted = can(operator, MONEY);
  const [answered, setAnswered] = useState<boolean | null>(null);
  const moneyVisible = answered ?? predicted;
  // What is actually asked for. A money ordering held in state while money
  // turns out to be withheld would earn a 403 — the ranking IS the figure —
  // so it falls back rather than being sent. It cannot normally happen: the
  // money headings are not rendered without the grant.
  const sent = resolveUserSort(sort, moneyVisible);

  const query = useAnalyticsTopUsers({
    since: search.since,
    until: search.until,
    days: search.days,
    sort: sent.sort,
    order: sent.order,
    limit: rows,
  });
  const answer = query.data;
  // The correction, REMEMBERED rather than read off the current answer.
  //
  // Adjusted during the render on purpose — React re-renders immediately, and
  // the inequality is what makes that terminate. An effect would be a frame
  // late, and reading `answer?.money_visible ?? predicted` directly would be
  // worse than either: `data` is undefined on a failed read, so a disagreeing
  // pair would flip the resolved sort back and forth between two query keys on
  // every render. Once the server has said, it has said.
  if (answer && answered !== answer.money_visible) {
    setAnswered(answer.money_visible);
  }

  const identityVisible = answer?.identity_visible ?? can(operator, IDENTITY);
  // Never assumed: micros per major unit is 10^6 for JPY and 10^8 for USD, so
  // guessing is a hundredfold error on one of the eight currencies the
  // platform transacts in. A window that earned nothing names no currency and
  // the columns are then headed "units", the convention money-units.ts sets.
  const exponent = currencyExponent(answer?.currency) ?? ASSUMED_EXPONENT;
  const unit = answer?.currency ?? "units";
  // The currencies the money columns are NOT in. An account billed in one of
  // them shows its requests and its tokens and ZERO money, which an ascending
  // money ordering then ranks at the top — the one reading of this table that
  // is actively wrong, so it is said rather than left to be discovered.
  const others = (answer?.currencies ?? []).filter((code) => code !== answer?.currency);

  const sortable = userSortColumns(moneyVisible);
  const onSort = (column: TopUserSort) => setSort((current) => nextUserSort(current, column));
  const heading = (label: string, column: TopUserSort, title: string) => (
    <UserSortHeader label={label} column={column} sort={sent} onSort={onSort} title={title} />
  );

  const columns: Array<Column<AnalyticsTopUser>> = [
    {
      key: "account",
      header: "Account",
      render: (row) => <AccountCell row={row} identityVisible={identityVisible} />,
    },
    {
      key: "applications",
      header: "Applications",
      render: (row) =>
        row.applications.length === 0 ? (
          <span className="esm-muted-text">none recorded</span>
        ) : (
          <ul className="esm-inline-list">
            {row.applications.map((app) => (
              <li key={app.id}>
                <Link to="/applications/$applicationId" params={{ applicationId: app.id }}>
                  <code>{app.slug}</code>
                </Link>
              </li>
            ))}
          </ul>
        ),
    },
    {
      key: "requests",
      header: heading(
        "Requests",
        "requests",
        "Requests this account made inside the window, and how many of them failed.",
      ),
      align: "end",
      render: (row) => (
        <>
          <div>{row.requests.toLocaleString()}</div>
          <div className="esm-muted-text">{row.failed_requests.toLocaleString()} failed</div>
        </>
      ),
    },
    {
      key: "tokens",
      header: heading(
        "Tokens",
        "tokens",
        "Every token kind the platform bills for, summed. The default ordering: one account streaming a million tokens costs more than a thousand accounts asking one question each.",
      ),
      align: "end",
      render: (row) => (
        <>
          <div>{row.total_tokens.toLocaleString()}</div>
          {/* All FIVE dimensions, not just in and out. The headline is their
              flat sum (TopUser.TotalTokens), so a subline naming two of them
              invites the reader to subtract and find the total "wrong" by
              however much cache and reasoning traffic the account did — and
              hides the two dimensions most likely to be the surprise on a
              bill. A dimension at zero is PRINTED, for the reason the customer
              portal prints it: a kind that vanishes when unreported cannot be
              told apart from a kind nobody used. */}
          <div className="esm-muted-text">
            {row.input_tokens.toLocaleString()} in, {row.output_tokens.toLocaleString()} out
          </div>
          <div className="esm-muted-text">
            {row.cached_input_tokens.toLocaleString()} cached,{" "}
            {row.cache_write_tokens.toLocaleString()} cache writes,{" "}
            {row.reasoning_tokens.toLocaleString()} reasoning
          </div>
        </>
      ),
    },
    {
      key: "reach",
      header: "Models / devices",
      align: "end",
      // "up to", because both figures are upper bounds and their names on the
      // wire say so: the rollup stores a per-day distinct count and never the
      // ids, so a model used on ten days contributes ten.
      render: (row) => (
        <>
          <div>up to {row.distinct_models_upper_bound.toLocaleString()} models</div>
          <div className="esm-muted-text">
            up to {row.distinct_devices_upper_bound.toLocaleString()} devices
          </div>
        </>
      ),
    },
  ];

  // The three money columns and the three money ORDERINGS appear together or
  // not at all, off one list, because they are the same fact: the ranking is
  // the figure. Filtering the columns by the sorts the operator may ask for is
  // what keeps a heading from existing that the server would refuse.
  const moneyColumns: Array<Column<AnalyticsTopUser>> = [
    {
      key: "cost",
      header: heading("Cost", "cost", `What this account's requests cost upstream, in ${unit}.`),
      align: "end",
      render: (row) => <MoneyCell micros={row.cost_micros} exponent={exponent} />,
    },
    {
      key: "revenue",
      header: heading(
        "Revenue",
        "revenue",
        `What this account was charged for them, in ${unit}. Top-ups and subscription charges are money moving into a wallet rather than revenue, and are not in this column.`,
      ),
      align: "end",
      render: (row) => <MoneyCell micros={row.sell_micros} exponent={exponent} />,
    },
    {
      key: "margin",
      header: heading(
        "Margin",
        "margin",
        `Revenue minus cost, in ${unit}. Negative is a real reading — a written-off request costs money that was never charged for.`,
      ),
      align: "end",
      render: (row) => <MoneyCell micros={row.margin_micros} exponent={exponent} />,
    },
  ];
  columns.push(...moneyColumns.filter((c) => sortable.indexOf(c.key as TopUserSort) !== -1));

  columns.push({
    key: "last_seen",
    header: heading(
      "Last seen",
      "last_seen",
      "The account's last request inside the window. Accounts the sweep recorded no times for sort LAST in both directions: unmeasured is not the same as dormant.",
    ),
    render: (row) =>
      row.last_request_at ? (
        when(row.last_request_at)
      ) : (
        // Not a dash and not "never": the rollup for this account carries no
        // timestamps, which is a gap in the measurement rather than a fact
        // about when they last used the platform.
        <span className="esm-not-recorded">not recorded</span>
      ),
  });

  return (
    <Card title="Top users">
      <p className="esm-muted-text">
        The accounts behind the figures above, ranked over the same window and then cut — so this is
        the top of the list, not the whole of it. Every row links to the account.
      </p>

      {/* A div and not a form: the select applies on change, so there is
          nothing to submit — and an empty form is a control that looks like it
          is waiting for an Enter that does nothing. */}
      <div className="esm-filters">
        <Field label="Rows" htmlFor="analytics-users-rows" hint="The server answers at most 200.">
          <Select
            id="analytics-users-rows"
            value={String(rows)}
            onChange={(event) => setRows(Number(event.target.value) || DEFAULT_ROWS)}
          >
            {ROW_OPTIONS.map((option) => (
              <option key={option} value={String(option)}>
                {option} rows
              </option>
            ))}
          </Select>
        </Field>
        {/* Only while REORDERING. The first load has its own spinner below,
            and two spinners on one card read as two things happening. */}
        {query.isFetching && !query.isPending ? <Spinner label="Reordering" /> : null}
      </div>

      {!moneyVisible ? (
        <p className="esm-muted-text">
          The cost, revenue and margin columns need <code>{MONEY}</code>, which this account does
          not hold. They are absent rather than blank, and so are the orderings that would rank by
          them — a ranking by revenue is the revenue, in ordinal form.
        </p>
      ) : null}
      {!identityVisible ? (
        <p className="esm-muted-text">
          Names and email addresses need <code>{IDENTITY}</code>, which this account does not hold.
          The ranking, the figures and the links to each account are unaffected.
        </p>
      ) : null}

      <ErrorAlert error={query.error} title="The leaderboard could not be loaded" />

      {answer && others.length > 0 ? (
        <Alert tone="warning" title="More than one currency earned in this window">
          The money columns are {answer.currency} alone. These days also carry {others.join(", ")},
          and an account billed in one of those shows its requests and its tokens with ZERO money —
          which an ascending money ordering ranks at the top.
        </Alert>
      ) : null}
      {answer?.truncated ? (
        <Alert tone="info" title="The leaderboard was cut short">
          More accounts used the platform in this window than the {answer.limit} rows below. The
          tail this dropped is the quiet end of the ordering above, so an account that is missing is
          one this page does not cover — not one at zero. Ask for more rows to see further down.
        </Alert>
      ) : null}

      {query.isPending && !query.error ? <Spinner label="Loading the leaderboard" /> : null}
      {answer ? (
        <DataTable<AnalyticsTopUser>
          rows={answer.users}
          rowKey={(row) => row.user_id}
          empty={
            // The two facts a leaderboard must never confuse, told apart by
            // the overview beside it: `available_from` is what says whether a
            // window was measured at all, and this route does not carry it.
            report.kind === "empty"
              ? "Nothing has been measured over these days, so there is nobody to rank — which is not the same as nobody having used the platform."
              : `No account made a request between ${answer.window.since} and ${answer.window.until}.`
          }
          columns={columns}
        />
      ) : null}
    </Card>
  );
}
